import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import QuickCapture from '@/components/QuickCapture';
import {
  Tray,
  Lightning,
  CheckCircle,
  Trash,
  ArrowRight,
  Clock,
  CalendarBlank,
  Tag,
  FunnelSimple,
  Phone,
  FileText,
  Users,
  Briefcase,
  Archive,
  SpinnerGap
} from '@phosphor-icons/react';

const API = `${window.location.origin}/api`;

const CATEGORY_ICONS = {
  call: Phone,
  meeting: Users,
  review: FileText,
  deadline: Clock,
  follow_up: ArrowRight,
  task: CheckCircle,
  other: Lightning,
};

const PRIORITY_STYLES = {
  high: 'bg-red-50 text-red-600 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-slate-50 text-slate-600 border-slate-200',
};

function Inbox({ user }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, all, archived
  const [converting, setConverting] = useState(null);
  const [matters, setMatters] = useState([]);
  const [showMatterPicker, setShowMatterPicker] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const params = {};
      if (filter === 'active') params.status = undefined; // default returns unprocessed+parsed
      else if (filter === 'archived') params.status = 'archived';
      else if (filter === 'all') params.status = undefined;

      const url = filter === 'archived'
        ? `${API}/inbox?status=archived`
        : `${API}/inbox`;
      const res = await axios.get(url, { withCredentials: true });
      setItems(res.data);
    } catch (err) {
      console.error('Failed to fetch inbox:', err);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => {
    axios.get(`${API}/matters`, { withCredentials: true })
      .then(res => setMatters(res.data)).catch(() => {});
  }, []);

  const handleCaptured = (newItem) => {
    setItems(prev => [newItem, ...prev]);
  };

  const convertToTask = async (item, matterId = null) => {
    setConverting(item.inbox_id);
    try {
      const payload = { convert_to: 'task' };
      if (matterId) payload.matter_id = matterId;
      await axios.post(`${API}/inbox/${item.inbox_id}/convert`, payload, { withCredentials: true });
      setItems(prev => prev.filter(i => i.inbox_id !== item.inbox_id));
      setShowMatterPicker(null);
    } catch (err) {
      console.error('Convert failed:', err);
    } finally { setConverting(null); }
  };

  const archiveItem = async (inboxId) => {
    try {
      await axios.delete(`${API}/inbox/${inboxId}`, { withCredentials: true });
      setItems(prev => prev.filter(i => i.inbox_id !== inboxId));
    } catch (err) {
      console.error('Archive failed:', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header user={user} />
      <main className="max-w-4xl mx-auto p-6 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-slate-900 mb-1 flex items-center gap-3">
              <Tray size={32} weight="duotone" className="text-navy" />
              Inbox
            </h1>
            <p className="text-sm md:text-base text-slate-500">Capture thoughts fast, organize later</p>
          </div>
        </div>

        {/* Quick Capture */}
        <div className="mb-6" data-testid="inbox-quick-capture">
          <QuickCapture onCaptured={handleCaptured} />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3 mb-5">
          <FunnelSimple size={16} className="text-slate-400" />
          {['active', 'archived'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200 ${
                filter === f ? 'bg-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`inbox-filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <span className="text-xs text-slate-400 ml-auto">{items.length} item{items.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Items */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-4 border-navy border-t-transparent" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-12 text-center" data-testid="inbox-empty">
            <Tray size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">
              {filter === 'archived' ? 'No archived items.' : 'Your inbox is clear. Capture something above!'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <AnimatePresence>
              {items.map((item) => {
                const parsed = item.parsed || {};
                const CategoryIcon = CATEGORY_ICONS[parsed.suggested_category] || Lightning;
                const isConverting = converting === item.inbox_id;

                return (
                  <motion.div
                    key={item.inbox_id}
                    layout
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20, height: 0 }}
                    transition={{ layout: { duration: 0.25 } }}
                    className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-sm hover:-translate-y-[1px] transition-all duration-200"
                    data-testid={`inbox-item-${item.inbox_id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Category icon */}
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <CategoryIcon size={18} weight="duotone" className="text-navy" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm text-slate-900 mb-1">
                          {parsed.title || item.raw_text}
                        </h3>

                        {/* Raw text (if different from title) */}
                        {parsed.title && parsed.title !== item.raw_text && (
                          <p className="text-xs text-slate-400 mb-2 italic">"{item.raw_text}"</p>
                        )}

                        {/* Parsed metadata */}
                        <div className="flex flex-wrap items-center gap-2">
                          {parsed.suggested_category && (
                            <span className="rounded-md px-2 py-0.5 text-[11px] font-medium border bg-blue-50 text-navy border-blue-200">
                              {parsed.suggested_category.replace('_', ' ')}
                            </span>
                          )}
                          {parsed.priority && (
                            <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium border ${PRIORITY_STYLES[parsed.priority] || ''}`}>
                              {parsed.priority}
                            </span>
                          )}
                          {parsed.due_date && (
                            <span className="flex items-center gap-1 text-xs text-slate-500">
                              <CalendarBlank size={12} /> {parsed.due_date}
                              {parsed.due_time && ` ${parsed.due_time}`}
                            </span>
                          )}
                          {parsed.matter_keywords?.length > 0 && parsed.matter_keywords.map((kw, i) => (
                            <span key={i} className="rounded-md px-2 py-0.5 text-[11px] font-medium border bg-slate-50 text-slate-600 border-slate-200 flex items-center gap-1">
                              <Tag size={10} />{kw}
                            </span>
                          ))}
                          {parsed.confidence && (
                            <span className={`text-[10px] font-medium ${
                              parsed.confidence === 'high' ? 'text-emerald-600' :
                              parsed.confidence === 'medium' ? 'text-amber-600' : 'text-slate-400'
                            }`}>
                              {parsed.confidence} confidence
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      {item.status !== 'archived' && (
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <button
                            onClick={() => convertToTask(item)}
                            disabled={isConverting}
                            className="bg-navy text-white hover:bg-navy-hover disabled:opacity-50 transition-all duration-200 font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 text-xs"
                            data-testid={`convert-task-${item.inbox_id}`}
                          >
                            {isConverting ? <SpinnerGap size={12} className="animate-spin" /> : <ArrowRight size={12} weight="bold" />}
                            Task
                          </button>
                          {matters.length > 0 && (
                            <button
                              onClick={() => setShowMatterPicker(showMatterPicker === item.inbox_id ? null : item.inbox_id)}
                              className="bg-blue-50 text-navy hover:bg-blue-100 transition-colors font-medium px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1"
                              data-testid={`convert-matter-${item.inbox_id}`}
                            >
                              <Briefcase size={12} />
                            </button>
                          )}
                          <button
                            onClick={() => archiveItem(item.inbox_id)}
                            className="text-slate-300 hover:text-slate-500 transition-colors p-1.5"
                            data-testid={`archive-${item.inbox_id}`}
                          >
                            <Archive size={16} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Matter picker dropdown */}
                    {showMatterPicker === item.inbox_id && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                        className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-xs font-medium text-slate-500 mb-2">Convert to task & link to matter:</p>
                        <div className="flex flex-wrap gap-2">
                          {matters.slice(0, 6).map(m => (
                            <button key={m.matter_id}
                              onClick={() => convertToTask(item, m.matter_id)}
                              className="bg-slate-50 hover:bg-blue-50 hover:text-navy border border-slate-200 rounded-md px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors"
                              data-testid={`link-matter-${m.matter_id}`}>
                              {m.name}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </main>
    </div>
  );
}

export default Inbox;
